import { Card } from '@thegpvc/ui';
import { text, cx } from '../../styles/theme';
import { Eyebrow } from './WorkDeliveredCard';
import type { AgentKeys, HashtagCount } from '../../types/api';

// Turn-signal colors: cool = flow (decisions, outcomes), warm = friction.
const SIGNAL_COLORS: Record<string, string> = {
  decision: 'bg-blue-600',
  outcome: 'bg-blue-400',
  request: 'bg-sky-300',
  'autonomous-win': 'bg-emerald-500',
  frustration: 'bg-amber-500',
  blocker: 'bg-rose-500',
  circular: 'bg-rose-400',
  intervention: 'bg-gray-400',
  correction: 'bg-amber-400',
  friction: 'bg-rose-400',
};

const NA = -1;

/**
 * FrictionQualityCard answers "how did it go?": the recovery and
 * intervention keys with plain-language captions, plus the turn-signal
 * distribution as a proportional bar.
 */
export function FrictionQualityCard({
  keys,
  hashtags,
  loading,
}: {
  keys?: AgentKeys;
  hashtags?: HashtagCount[];
  loading: boolean;
}) {
  const recovery = keys?.recovery?.current ?? NA;
  const intervention = keys?.intervention?.current ?? NA;
  const moments = keys?.frictionMoments ?? 0;
  const recovered = keys?.recoveredMoments ?? 0;

  const tags = (hashtags ?? [])
    .map((h) => ({ tag: h.tag, count: parseInt(h.totalCount, 10) || 0 }))
    .filter((h) => h.count > 0);
  const tagTotal = tags.reduce((sum, h) => sum + h.count, 0);

  return (
    <Card>
      <Card.Body>
        <Eyebrow>Friction &amp; quality</Eyebrow>

        <div className="mt-3">
          <div className="flex items-baseline gap-2">
            <span className={cx('text-3xl font-bold tabular-nums leading-none', text.primary)}>
              {loading ? '…' : recovery === NA ? '—' : `${Math.round(recovery)}%`}
            </span>
            <span className={cx('text-base font-medium', text.primary)}>of friction recovered</span>
          </div>
          <p className={cx('mt-1 text-sm', text.secondary)}>
            {recovery === NA
              ? 'No friction moments in this period.'
              : `${recovered} of ${moments} friction moments reached an outcome without you stepping in.`}
          </p>
        </div>

        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-navy-800">
          <div className="flex items-baseline gap-2">
            <span className={cx('font-mono text-2xl font-bold tabular-nums leading-none', text.primary)}>
              {loading ? '…' : intervention === NA ? '—' : intervention.toFixed(1)}
            </span>
            <span className={cx('text-base font-medium', text.primary)}>corrections per 100 prompts</span>
          </div>
          <p className={cx('mt-1 text-sm', text.secondary)}>
            How often you had to redirect an agent mid-task. Lower is better.
          </p>
        </div>

        {tagTotal > 0 && (
          <div className="mt-5">
            <span className={cx('text-xs font-medium uppercase tracking-wider', text.muted)}>Turn signals</span>
            {/* The segments encode each tag's share only by color/width, so the
                distribution is invisible to assistive tech. Mirror the shared
                bar primitives (CompositionBar, TimeRibbon) by giving the track
                a role="img" with an aria-label carrying the same tag/count data
                as the per-segment tooltips. */}
            <div
              role="img"
              aria-label={`Turn signals: ${tags.map((h) => `${h.tag} ${h.count}`).join(', ')}`}
              className="mt-2 flex h-2.5 w-full gap-px overflow-hidden rounded-full"
            >
              {tags.map((h) => (
                <div
                  key={h.tag}
                  aria-hidden="true"
                  className={SIGNAL_COLORS[h.tag] ?? 'bg-gray-300'}
                  style={{ width: `${Math.max(1, (h.count / tagTotal) * 100)}%` }}
                  title={`${h.tag}: ${h.count}`}
                />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {tags.map((h) => (
                <span key={h.tag} className="flex items-center gap-1.5 text-xs">
                  <span className={cx('h-2 w-2 rounded-sm', SIGNAL_COLORS[h.tag] ?? 'bg-gray-300')} />
                  <span className={text.secondary}>{h.tag}</span>
                  <span className={cx('font-mono tabular-nums', text.muted)}>{h.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
